import { derived, get, writable } from "svelte/store";
import type { User } from "$apis/parkito/index.ts";
import { JsonDecoder } from "ts.data.json";
import { userDecoder } from "./decoders.ts";

const STORAGE_KEY = "parkando-jwt";

export interface Authenticated {
  jwt: string;
  user?: User;
  masquerading?: boolean;
}

type SessionState = Authenticated | null;

const sessionDecoder = JsonDecoder.object<Authenticated>({
  jwt: JsonDecoder.string,
  user: JsonDecoder.optional(userDecoder),
  masquerading: JsonDecoder.optional(JsonDecoder.boolean),
}, "userDecoder");

function parseSessionFromLocalStorage(): SessionState {
  const storedStr = globalThis.localStorage.getItem(STORAGE_KEY);

  if (storedStr) {
    let err: string;
    try {
      const res = sessionDecoder.decode(JSON.parse(storedStr));

      if (res.isOk()) {
        return res.value;
      } else {
        err = res.error;
      }
    } catch (ex: any) {
      if (import.meta.env.DEV) console.log("err: ", ex);
      err = ex;
    }

    if (import.meta.env.DEV) {
      console.log(`Clearing session from localStorage due to error: ${err}`);
    }
    globalThis.localStorage.removeItem(STORAGE_KEY);
  } else {
    if (import.meta.env.DEV) {
      console.log(
        "No session with storage key",
        STORAGE_KEY,
        " in localStorage",
      );
    }
  }

  return null;
}

function setupStore() {
  const sessionState = writable<SessionState>(
    parseSessionFromLocalStorage(),
  );
  const { subscribe, set } = sessionState;

  addEventListener("storage", (ev) => {
    if (ev.key === STORAGE_KEY) {
      const state = parseSessionFromLocalStorage();
      set(state);
    }
  });

  return {
    subscribe,

    clear: () => {
      globalThis.localStorage.removeItem(STORAGE_KEY);
      set(null);
    },

    setState: (jwt: string, user?: User, masquerading: boolean = false) => {
      const session = { jwt: jwt, user: user, masquerading };
      const s = JSON.stringify(session);
      globalThis.localStorage.setItem(STORAGE_KEY, s);

      set(session);
    },

    isMasqing: derived(sessionState, (state) => !!state?.masquerading),
    getFormattedName: (): string | undefined => {
      return get(sessionState)?.user?.names?.full
        .split(" ")
        .map((n: string) => n[0].toUpperCase() + n.slice(1).toLowerCase())
        .join(" ");
    },
  };
}

export const session = setupStore();
